/**
 * WorkItemTimeline — J7-W5 task 6c-3.
 *
 * Gantt-lite SVG renderer for the Control Tower timeline view.
 *  - X-axis: time, from min(createdAt) to max(dueDate ?? updatedAt).
 *  - Y-axis: rows grouped by domain; each work item is one bar.
 *  - Bar color: status (mirrors the status badge palette).
 *  - Hover: native SVG <title> tooltip with the work item title + status + dates.
 *  - Click: invokes onSelect(item) so the page can open the WorkItemDrawer.
 *
 * Why SVG rather than Recharts?
 *  - Recharts is great for time-series; the data here is one bar per row
 *    spanning a [start, end] window. A 60-line raw SVG is smaller, sharper,
 *    and avoids forcing a chart-axis/legend that the design doesn't want.
 *
 * Empty state is rendered by the parent (ControlTowerPage) so this file
 * stays a pure renderer.
 */

import React, { useMemo } from 'react';
import { t } from '@/lib/i18n';
import { formatDate } from '@/lib/localeFormatters';
import type { RorosWorkItemDTO } from '@/types/roros/control-tower';
import type { RorosDomainDTO } from '@/types/roros/domains';

interface WorkItemTimelineProps {
  items: RorosWorkItemDTO[];
  domains: RorosDomainDTO[];
  onSelect: (item: RorosWorkItemDTO) => void;
}

const STATUS_COLOR: Record<string, string> = {
  backlog: '#9ca3af',                // gray-400
  ready: '#60a5fa',                  // blue-400
  in_progress: '#3b82f6',            // blue-500
  done: '#a7f3d0',                   // emerald-200
  quality_confirmation: '#fbbf24',   // amber-400
  released: '#10b981',               // emerald-500
  blocked: '#ef4444',                // red-500
  escalated: '#dc2626',              // red-600
};

const ROW_HEIGHT = 28;
const ROW_GAP = 4;
const DOMAIN_HEADER_HEIGHT = 22;
const LEFT_LABEL_WIDTH = 200;
const CHART_WIDTH = 720;
const CHART_PADDING_X = 16;

export function WorkItemTimeline({ items, domains, onSelect }: WorkItemTimelineProps) {
  const grouped = useMemo(() => {
    const map = new Map<string, RorosWorkItemDTO[]>();
    for (const it of items) {
      const arr = map.get(it.domainId) ?? [];
      arr.push(it);
      map.set(it.domainId, arr);
    }
    return Array.from(map.entries()).map(([domainId, rows]) => ({
      domainId,
      domainName: domains.find((d) => d.id === domainId)?.name ?? domainId.slice(0, 8),
      rows: rows.sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    }));
  }, [items, domains]);

  const { minMs, maxMs } = useMemo(() => {
    if (items.length === 0) return { minMs: 0, maxMs: 1 };
    let min = Infinity;
    let max = -Infinity;
    for (const it of items) {
      const start = new Date(it.createdAt).getTime();
      const end = new Date(it.dueDate ?? it.updatedAt).getTime();
      if (start < min) min = start;
      if (end > max) max = end;
    }
    if (min === max) max = min + 86_400_000; // pad 1 day so width is non-zero
    return { minMs: min, maxMs: max };
  }, [items]);

  const totalRows = grouped.reduce((acc, g) => acc + g.rows.length, 0);
  const svgHeight = grouped.length * DOMAIN_HEADER_HEIGHT + totalRows * (ROW_HEIGHT + ROW_GAP) + 12;
  const innerWidth = CHART_WIDTH - LEFT_LABEL_WIDTH - CHART_PADDING_X * 2;

  function xFor(ms: number): number {
    if (maxMs === minMs) return LEFT_LABEL_WIDTH + CHART_PADDING_X;
    const pct = (ms - minMs) / (maxMs - minMs);
    return LEFT_LABEL_WIDTH + CHART_PADDING_X + pct * innerWidth;
  }

  let y = 8;

  return (
    <div className="overflow-x-auto rounded-lg border border-border" data-testid="work-item-timeline">
      <svg width={CHART_WIDTH} height={svgHeight} role="img" aria-label={t('Timeline')}>
        {grouped.map((group) => {
          const headerY = y;
          y += DOMAIN_HEADER_HEIGHT;
          const rowEls = group.rows.map((it) => {
            const startMs = new Date(it.createdAt).getTime();
            const endMs = new Date(it.dueDate ?? it.updatedAt).getTime();
            const x1 = xFor(startMs);
            const x2 = Math.max(xFor(endMs), x1 + 4);
            const rowY = y;
            y += ROW_HEIGHT + ROW_GAP;
            const tooltip = `${it.title}\n${it.status}\n${formatDate(it.createdAt)} → ${it.dueDate ? formatDate(it.dueDate) : '—'}`;
            return (
              <g
                key={it.id}
                className="cursor-pointer"
                onClick={() => onSelect(it)}
                data-testid={`timeline-bar-${it.id}`}
              >
                <title>{tooltip}</title>
                <text
                  x={CHART_PADDING_X}
                  y={rowY + ROW_HEIGHT / 2 + 4}
                  fontSize="12"
                  fill="currentColor"
                  className="fill-muted-foreground"
                >
                  {it.title.length > 26 ? `${it.title.slice(0, 24)}…` : it.title}
                </text>
                <rect
                  x={x1}
                  y={rowY + 2}
                  width={x2 - x1}
                  height={ROW_HEIGHT - 4}
                  rx={4}
                  fill={STATUS_COLOR[it.status] ?? '#9ca3af'}
                  opacity={0.85}
                />
              </g>
            );
          });
          return (
            <g key={group.domainId}>
              <text
                x={CHART_PADDING_X}
                y={headerY + DOMAIN_HEADER_HEIGHT - 6}
                fontSize="11"
                fontWeight={600}
                className="fill-foreground"
              >
                {group.domainName.toUpperCase()}
              </text>
              {rowEls}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
